<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { injectPageLoadTracker } from '@integro/core';
import { ItpLoadingIndicator, ItpLoadError, ItpReloadError } from '../loading';
import { ItpProgressLinear } from '../progress';
import { useDelayedFlag } from '../../composables';
import { useKeepAliveScroll } from './useKeepAliveScroll';

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    reloading?: boolean;
    loadingSpinner?: boolean;
    loaded?: boolean;
    error?: Error | string | null;
    modal?: boolean;
    card?: boolean;
    modalBindings?: Record<string, any>;
    size?: string;
    modalName?: string;
    header?: string;
    noShow?: boolean;
  }>(),
  {
    loading: false,
    reloading: false,
    loadingSpinner: false,
    loaded: false,
    error: null,
    modal: false,
    card: false,
    modalBindings: () => ({}),
    size: undefined,
    modalName: undefined,
    header: undefined,
    noShow: false,
  },
);

const emit = defineEmits<{
  (e: 'close', args?: any): void;
  (e: 'reload'): void;
}>();

const pageRoot = useTemplateRef<HTMLElement>('pageRoot');
useKeepAliveScroll(pageRoot);

const loadTracker = injectPageLoadTracker();

const revealed = computed(() =>
  loadTracker ? loadTracker.state.value === 'loaded' : props.loaded,
);

const failure = computed(
  () =>
    props.error ??
    (loadTracker ? (loadTracker.error.value as any) : null) ??
    null,
);

const showError = computed(() => !!failure.value && !revealed.value);

const retrying = computed(
  () =>
    props.reloading || (!!loadTracker && loadTracker.state.value === 'loading'),
);

const pageLoading = computed(() => !revealed.value && !showError.value);

const slowLoad = useDelayedFlag(pageLoading);

const showIndicator = computed(() => slowLoad.value && !showError.value);

const contentHidden = computed(() => pageLoading.value || showIndicator.value);

const slowReload = useDelayedFlag(() => !!loadTracker?.reloading.value);

const reloadingInPlace = computed(
  () =>
    (props.loading || slowReload.value) &&
    !contentHidden.value &&
    !showError.value,
);

const isBusy = computed(() => contentHidden.value || reloadingInPlace.value);

const componentName = computed(() => (props.modal ? 'ItpModal' : 'div'));

const componentBindings = computed(() => {
  if (!props.modal) {
    return { class: 'h-100 position-relative' };
  }
  let result: Record<string, any> = { ...props.modalBindings };
  if (props.size) {
    result.size = props.size;
  }
  if (props.header) {
    result.header = props.header;
  }
  if (props.noShow !== undefined) {
    result.noShow = props.noShow;
  }

  result.loading = pageLoading.value;
  result.contentMounted = props.loaded;
  if (showError.value) {
    result.noPrimaryButton = true;
  }
  return result;
});

function onClose(args: any) {
  emit('close', args);
}

function onReload() {
  if (loadTracker) {
    loadTracker.retry();
    return;
  }
  emit('reload');
}
</script>

<template>
  <component :is="componentName" v-bind="componentBindings" @close="onClose">
    <template v-if="modal" #header>
      <slot name="header" />
    </template>

    <template v-if="modal" #headerCenter>
      <slot name="headerCenter" />
    </template>

    <ItpReloadError
      v-if="modal && loaded && !showError && error"
      :error="error"
      :reloading="reloading"
    />

    <slot v-if="modal && !showError" />

    <ItpLoadError
      v-if="modal && showError"
      :error="failure"
      :reloading="retrying"
      @reload="onReload"
    />

    <template v-if="modal" #footer>
      <slot name="footer" />
    </template>

    <div
      v-if="!modal && !card"
      ref="pageRoot"
      class="page--v2"
      :class="{ 'is-loading': contentHidden }"
      :aria-busy="isBusy"
    >
      <slot v-if="loaded && !showError" name="header" />
      <ItpLoadError
        v-if="showError"
        class="flex-grow-1"
        style="min-height: 0"
        :error="failure"
        :reloading="retrying"
        @reload="onReload"
      />
      <ItpReloadError
        v-if="loaded && !showError && error"
        :error="error"
        :reloading="reloading"
      />
      <div
        v-if="loaded && !showError"
        class="overflow-hidden d-flex flex-column"
        :class="{ 'itp-busy-content': reloadingInPlace }"
        style="flex: 1; min-height: 0"
      >
        <slot />
      </div>
      <slot v-if="loaded && !showError" name="footer" />
    </div>

    <ItpLoadingIndicator
      v-if="!modal && !card && showIndicator"
      class="itp-page__indicator"
      fill
    />

    <ItpProgressLinear
      v-if="!modal && !card && reloadingInPlace"
      class="itp-page__refresh"
    />

    <div
      v-if="!modal && card"
      ref="pageRoot"
      class="page--v2 overflow-auto bg-page"
      :class="{ 'is-loading': contentHidden }"
      :aria-busy="isBusy"
    >
      <div class="container-lg py-4 h-100">
        <div class="card shadow-sm h-100">
          <slot v-if="loaded && !showError" name="header" />
          <ItpLoadError
            v-if="showError"
            class="flex-grow-1"
            style="min-height: 0"
            :error="failure"
            :reloading="retrying"
            @reload="onReload"
          />
          <ItpReloadError
            v-if="loaded && !showError && error"
            :error="error"
            :reloading="reloading"
          />
          <div
            v-if="loaded && !showError"
            class="overflow-hidden d-flex flex-column"
            :class="{ 'itp-busy-content': reloadingInPlace }"
            style="flex: 1; min-height: 0"
          >
            <slot />
          </div>
          <slot v-if="loaded && !showError" name="footer" />
        </div>
      </div>
    </div>

    <ItpLoadingIndicator
      v-if="!modal && card && showIndicator"
      class="itp-page__indicator"
      fill
    />

    <ItpProgressLinear
      v-if="!modal && card && reloadingInPlace"
      class="itp-page__refresh"
    />
  </component>
</template>
